/**
 * What this operator likes, kept in this browser.
 *
 * The robot remembers what it was last set to, which is the right home for it -- the arm that
 * is picking should not forget its speed because a different laptop connected. But an operator
 * who prefers a narrow gripper and a slow close wants that on whichever arm they pick up, and
 * wants it back tomorrow. So preferences are remembered here too, and applied when they take
 * the controls rather than merely by opening the page: until you are driving, changing the
 * arm's settings is not yours to do.
 */
import type { Settings } from "../adamo/types";

const KEY = "operator.preferences";

export type Preferences = Partial<Settings>;

export function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Preferences) : {};
  } catch {
    return {};   // a private window simply has none
  }
}

export function rememberPreference<K extends keyof Settings>(key: K, value: Settings[K]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...loadPreferences(), [key]: value }));
  } catch {
    // nothing to do: the preference lives for this session only
  }
}

export function forgetPreferences(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // already gone as far as anyone can tell
  }
}

/** Which of the remembered values the robot is not already set to. */
export function differences(preferences: Preferences, actual: Settings): [keyof Settings, unknown][] {
  return (Object.entries(preferences) as [keyof Settings, unknown][]).filter(([key, value]) => {
    const current = actual[key];
    return typeof value === "number" && typeof current === "number"
      ? Math.abs(value - current) > 1e-3
      : value !== current;
  });
}
